import { i18n } from '@/app/i18n'
import { ApiError } from '@/shared/api/http'
import type { ApiErrorBody } from '@/features/auth/domain/auth.types'

const hasMessage = (body: unknown): body is ApiErrorBody =>
  Boolean(body && typeof body === 'object' && typeof (body as ApiErrorBody).message === 'string')

/** Localized last resort for callers with no `t` in scope (stores). */
export const getGenericErrorMessage = () => i18n.global.t('common.errors.unexpected')

/**
 * Reads the backend's already-localized error string (`{ message }`) from an ApiError, falling
 * back to a caller-supplied message for network/unknown errors. Note `ApiError.message` itself is
 * the useless "Request failed with status N", so always route user-facing errors through here.
 *
 * The backend sends no machine-readable `code`, so a localized business error (`branch.not-found`)
 * and a developer-facing rule violation ("Total eggs (60000) exceeds Setter capacity") are
 * indistinguishable here — the rules are validated client-side instead, so a rule 400 reaching a
 * user is a client bug, not a case to hide (hatchery.md §2, §8).
 */
export const getApiErrorMessage = (error: unknown, fallback: string = getGenericErrorMessage()): string => {
  if (error instanceof ApiError && hasMessage(error.body)) {
    return error.body.message
  }

  return fallback
}

/** An aborted request is not an error — it must never land in an error banner. */
export const isCanceledError = (error: unknown) =>
  error instanceof Error && (error.name === 'CanceledError' || error.name === 'AbortError')
